"use client";

import type React from "react";
import {
  Box,
  Container,
  VStack,
  Heading,
  Text,
  Link as ChakraLink,
  Flex,
  Button,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";

const TermsOfService: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Box minH="100vh" bg="white">
      {/* Navigation */}
      <Box
        as="nav"
        position="sticky"
        top={0}
        zIndex={10}
        bg="white"
        borderBottom="1px"
        borderColor="gray.200"
        boxShadow="sm"
      >
        <Container maxW="7xl" py={4}>
          <Flex justify="space-between" align="center">
            <Box
              as="img"
              src={process.env.REACT_APP_MAIN_LOGO_URL}
              alt="LeadiumX"
              h="50px"
              cursor="pointer"
              onClick={() => navigate("/")}
            />
            <Button
              variant="brandGradient"
              size="md"
              onClick={() => navigate("/signin")}
            >
              Login
            </Button>
          </Flex>
        </Container>
      </Box>

      <Container maxW="4xl" py={12}>
        <VStack spacing={8} align="start">
          <Heading as="h1" fontSize="4xl">
            Terms of Service
          </Heading>
          <Text fontSize="sm" color="gray.600">
            Last Updated: 04 November 2025
          </Text>

          <VStack
            spacing={6}
            align="start"
            fontSize="md"
            lineHeight="tall"
            color="gray.700"
          >
            <Text>
              These Terms of Service (the "Terms" or "Agreement") constitute a
              contract between LeadiumX ("LeadiumX," "we," "us," or "our") and
              you ("you," "your," or "Customer"). They govern your access to and
              use of the LeadiumX website, platform, and related services
              (collectively, the "Services"). By signing up, accessing, or using
              our Services, you agree to be bound by these Terms and our Privacy
              Policy.
            </Text>
            <Text>
              If you are using the Services on behalf of an entity, you
              represent that you have authority to bind that entity. If you do
              not agree to these Terms, you must not use our Services.
            </Text>

            <Box>
              <Heading as="h3" fontSize="xl" mb={3}>
                1. Services & Scope
              </Heading>
              <VStack align="start" spacing={2}>
                <Text>
                  <Text as="span" fontWeight="semibold">
                    Core Services.
                  </Text>{" "}
                  LeadiumX provides white-labeled AI Framework, tools, and
                  solutions (including APIs, dashboards, and integration
                  capabilities) to enable clients to build, manage, and operate
                  AI-driven lead-generation, calling/communication, analytics,
                  and related operations ("Services").
                </Text>
                <Text>
                  <Text as="span" fontWeight="semibold">
                    Professional Services & Custom Work.
                  </Text>{" "}
                  In addition to the standard features, we may offer consulting,
                  integration, strategy, optimization, onboarding support, or
                  other bespoke services, upon separate agreement.
                </Text>
              </VStack>
            </Box>

            <Box>
              <Heading as="h3" fontSize="xl" mb={3}>
                2. Fees, Billing & Payment
              </Heading>
              <VStack align="start" spacing={2}>
                <Text>
                  <Text as="span" fontWeight="semibold">
                    Subscription & Charges.
                  </Text>{" "}
                  Our service plans, pricing, and feature tiers are set forth on
                  our pricing page. Services are charged on a periodic basis
                  (e.g. monthly or yearly) ("Subscription Period"). You
                  authorize us to bill your chosen payment method (e.g., credit
                  card) for all fees plus any applicable taxes, without
                  requiring signed invoices.
                </Text>
                <Text>
                  <Text as="span" fontWeight="semibold">
                    Non-Refundability.
                  </Text>{" "}
                  Except as required by law, amounts paid are non-refundable.
                </Text>
                <Text>
                  <Text as="span" fontWeight="semibold">
                    Automatic Renewal.
                  </Text>{" "}
                  Unless you cancel before the end of the current period, your
                  subscription will automatically renew for the next period and
                  you will be charged accordingly.
                </Text>
                <Text>
                  <Text as="span" fontWeight="semibold">
                    Payment Failures & Late Fees.
                  </Text>{" "}
                  If payment fails, we may issue an invoice with a due date
                  (e.g. 14 days). If still unpaid, we may grant a grace period
                  (e.g. 7 days). After that, we may suspend or terminate
                  Services and charge a late fee (e.g. up to 10% of overdue
                  amount) or engage third-party collection. Collection costs
                  (including legal fees) are your responsibility.
                </Text>
              </VStack>
            </Box>

            <Box>
              <Heading as="h3" fontSize="xl" mb={3}>
                3. Customer Obligations
              </Heading>
              <Text mb={2}>You agree that:</Text>
              <VStack align="start" spacing={1} pl={6}>
                <Text>
                  • You are solely responsible for your use of the Services, the
                  content or data you input or upload, and ensuring compliance
                  with applicable laws.
                </Text>
                <Text>
                  • You must maintain the confidentiality of your account
                  credentials.
                </Text>
                <Text>
                  • You shall not grant third parties access other than
                  authorized users under your account.
                </Text>
                <Text>
                  • You will comply with all legal requirements governing your
                  use (calls, messaging, data, privacy, etc.).
                </Text>
              </VStack>
            </Box>

            <Box>
              <Heading as="h3" fontSize="xl" mb={3}>
                4. Acceptable Use Policy & Prohibited Uses
              </Heading>

              <Heading as="h4" fontSize="lg" mb={2} fontWeight="semibold">
                4.1 General Conduct
              </Heading>
              <Text mb={2}>You must not use the Services to:</Text>
              <VStack align="start" spacing={1} pl={6}>
                <Text>
                  • Violate laws or rights of others (privacy, defamation, etc.)
                </Text>
                <Text>
                  • Engage in harassing, abusive, fraudulent, deceptive, or
                  otherwise improper behavior
                </Text>
                <Text>
                  • Interfere with our systems or other users' use of Services
                </Text>
                <Text>
                  • Circumvent security, manipulate features, or misuse the
                  system
                </Text>
              </VStack>

              <Heading
                as="h4"
                fontSize="lg"
                mb={2}
                mt={4}
                fontWeight="semibold"
              >
                4.2 Telemarketing & Communications Compliance
              </Heading>
              <Text mb={2}>
                Because many use our Services for outreach and communications,
                you must:
              </Text>
              <VStack align="start" spacing={1} pl={6}>
                <Text>
                  • Adhere to all relevant laws (e.g. national telemarketing
                  rules, Do-Not-Call registries, consent requirements)
                </Text>
                <Text>• Maintain documentation of consent when required</Text>
                <Text>
                  • Provide clear disclosures (e.g. AI voice use or that calls
                  may be recorded)
                </Text>
                <Text>
                  • Scrub your call lists against DNC or similar registries
                  regularly (minimum frequency, e.g. every 30 days)
                </Text>
                <Text>• Permit us to audit your usage for compliance</Text>
              </VStack>
            </Box>

            <Box>
              <Heading as="h3" fontSize="xl" mb={3}>
                5. Indemnification
              </Heading>
              <Text mb={3}>
                You agree to indemnify, defend, and hold LeadiumX, its
                affiliates, officers, directors, employees, agents, and third
                parties harmless from any claims, liabilities, damages, costs,
                or expenses (including reasonable attorneys' fees) arising from:
              </Text>
              <VStack align="start" spacing={1} pl={6}>
                <Text>• Your breach of these Terms</Text>
                <Text>• Your use (or misuse) of the Services</Text>
                <Text>• Violation of rights of third parties</Text>
                <Text>• Noncompliance with laws or regulations</Text>
              </VStack>
            </Box>

            <Box>
              <Heading as="h3" fontSize="xl" mb={3}>
                6. Warranties; Disclaimer
              </Heading>
              <Text>
                <Text as="span" fontWeight="semibold">
                  No Warranty.
                </Text>{" "}
                The Services are provided "as is," "as available." LeadiumX
                disclaims all implied warranties, including fitness for a
                particular purpose, merchantability, or non-infringement. We do
                not guarantee uninterrupted, error-free, or secure operation. We
                do not promise that results from the Services will meet your
                expectations.
              </Text>
            </Box>

            <Box>
              <Heading as="h3" fontSize="xl" mb={3}>
                9. Limitation of Liability
              </Heading>
              <Text mb={3}>
                Under no circumstances will LeadiumX (or its affiliates) be
                liable for indirect, incidental, special, consequential, or
                exemplary damages, including lost profits, lost data, or
                business interruption, even if advised of the possibility.
              </Text>
              <Text>
                Our total liability (for all claims combined) will be limited to
                the greater of: (a) the amount you paid for the Service in the
                12 months preceding the claim, or (b) a nominal figure (e.g. USD
                $100) if you are using a free or trial version.
              </Text>
            </Box>

            <Box>
              <Heading as="h3" fontSize="xl" mb={3}>
                10. Intellectual Property & Licenses
              </Heading>
              <VStack align="start" spacing={2}>
                <Text>
                  <Text as="span" fontWeight="semibold">
                    Ownership.
                  </Text>{" "}
                  All rights, title, and interest in the Services, software,
                  content, platform, technology, algorithms, trademarks, and
                  models remain with LeadiumX or its licensors—except for
                  user-provided content.
                </Text>
                <Text>
                  <Text as="span" fontWeight="semibold">
                    License to You.
                  </Text>{" "}
                  Subject to compliance with these Terms, we grant you a
                  limited, non-exclusive, non-transferable, revocable license to
                  access and use the Services for your internal business
                  purposes. You may not sublicense, sell, lease,
                  reverse-engineer, or create derivative works.
                </Text>
              </VStack>
            </Box>

            <Box>
              <Heading as="h3" fontSize="xl" mb={3}>
                17. Dispute Resolution & Governing Law
              </Heading>
              <VStack align="start" spacing={2}>
                <Text>
                  <Text as="span" fontWeight="semibold">
                    Governing Law.
                  </Text>{" "}
                  These Terms are governed by the laws of United States.
                </Text>
                <Text>
                  <Text as="span" fontWeight="semibold">
                    Jurisdiction & Venue.
                  </Text>{" "}
                  You submit to the exclusive jurisdiction and venue of courts
                  located in Florida for disputes.
                </Text>
                <Text>
                  <Text as="span" fontWeight="semibold">
                    Registered Address.
                  </Text>{" "}
                  1650 Sand Lake Rd Suite 201, Orlando, FL 32809
                </Text>
              </VStack>
            </Box>

            <Box>
              <Heading as="h3" fontSize="xl" mb={3}>
                24. Contact
              </Heading>
              <Text mb={2}>
                If you have questions, disputes, or want to send notices, please
                contact:
              </Text>
              <Box pl={6}>
                <Text fontWeight="semibold">LeadiumX</Text>
                <Text>1650 Sand Lake Road Orlando FL 32809</Text>
                <Text>
                  Email:{" "}
                  <ChakraLink
                    href="mailto:Support@leadiumx.ai"
                    color="primary.600"
                  >
                    Support@leadiumx.ai
                  </ChakraLink>
                </Text>
              </Box>
            </Box>
          </VStack>
        </VStack>
      </Container>
    </Box>
  );
};

export default TermsOfService;
